import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Eye, Pencil, Trash2, Stethoscope, ArrowRight, Image as ImageIcon } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatusBadge from '../../components/StatusBadge';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../utils/roles';
import * as pkgApi from '../../api/packages.api';
import * as quizApi from '../../api/quiz.api';
import { errorMessage } from '../../api/client';
import { formatMoney } from '../../utils/format';

export function PatientCardView({ items, quiz }) {
  const navigate = useNavigate();

  if (items.length === 0) {
    return <EmptyState
      icon={Stethoscope}
      title="No treatment plans available right now"
      description="Please check back soon — new plans are added regularly."
    />;
  }

  function onGetStarted(pkg) {
    if (!quiz) {
      navigate(`/quiz?next=/packages/${pkg._id}`);
      return;
    }
    if (quiz.eligibilityStatus === 'NOT_ELIGIBLE') {
      toast.error('You\'re not eligible right now — please consult a doctor first.');
      return;
    }
    navigate(`/orders/checkout/${pkg._id}`);
  }

  return (
    <div className="grid grid-3">
      {items.map((p) => {
        const banner = p.bannerUrl ? pkgApi.packageBannerUrl(p) : '';
        return (
          <div
            key={p._id}
            className="card"
            style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}
          >
            {/* Banner image area */}
            <Link
              to={`/packages/${p._id}`}
              style={{
                display: 'block',
                width: '100%',
                height: 160,
                background: banner ? '#f1f5f9' : 'linear-gradient(135deg, var(--brand-50), var(--brand-100, #e0e7ff))',
                borderBottom: '1px solid var(--border)',
              }}
            >
              {banner ? (
                <img
                  src={banner}
                  alt={`${p.name} banner`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: 'var(--brand-700)'
                }}>
                  <ImageIcon size={36} />
                </div>
              )}
            </Link>

            <div style={{ padding: 'var(--s-4)', display: 'flex', flexDirection: 'column', flex: 1 }}>
              <div className="row between" style={{ marginBottom: 8 }}>
                <h3 style={{ fontSize: 18 }}>{p.name}</h3>
                {p.isActive
                  ? <StatusBadge tone="success">Available</StatusBadge>
                  : <StatusBadge tone="neutral">Inactive</StatusBadge>}
              </div>
              <div className="muted text-sm" style={{ marginBottom: 12, minHeight: 40 }}>
                {p.description || 'Doctor-prescribed GLP-1 weight-loss treatment plan.'}
              </div>
              {p.tests?.length > 0 && (
                <div className="tag-stack" style={{ marginBottom: 12 }}>
                  {p.tests.slice(0, 4).map((t) => (
                    <span key={t} className="badge neutral">{t}</span>
                  ))}
                  {p.tests.length > 4 && <span className="badge neutral">+{p.tests.length - 4} more</span>}
                </div>
              )}
              <div className="spacer" />
              <div className="row between" style={{ marginTop: 12, alignItems: 'flex-end' }}>
                <div>
                  <div className="muted text-xs">From</div>
                  <div className="bold text-xl">{formatMoney(p.price, p.currency || 'MYR')}</div>
                </div>
                <div className="row" style={{ gap: 6 }}>
                  <Link to={`/packages/${p._id}`} className="btn btn-outline btn-sm">Details</Link>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => onGetStarted(p)}
                    disabled={!p.isActive}
                  >
                    Get Started <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Packages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(user?.role);
  const isPatient = user?.role === ROLES.USER;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [confirming, setConfirming] = useState(null); // pkg id
  const [deleting, setDeleting] = useState(false);
  const [quiz, setQuiz] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const data = await pkgApi.listPackages();
      setItems(data || []);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // Patients gate "Get Started" on their latest quiz result.
  useEffect(() => {
    if (!isPatient) return;
    quizApi.myLatestQuiz().then(setQuiz).catch(() => {});
  }, [isPatient]);

  async function onDelete() {
    if (!confirming) return;
    setDeleting(true);
    try {
      await pkgApi.deletePackage(confirming);
      toast.success('Package deleted');
      setConfirming(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <PageHeader
        title={isPatient ? 'Treatment plans' : 'Treatment plans'}
        subtitle={isPatient
          ? 'Choose a 1-, 2-, or 3-bottle GLP-1 plan and get matched with a clinic.'
          : 'Manage all treatment plans (bottles · weeks · expected loss).'}
        actions={
          isAdmin ? (
            <button className="btn btn-primary" onClick={() => navigate('/packages/new')}>
              <Plus size={16} /> New package
            </button>
          ) : null
        }
      />

      {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}

      {isPatient && quiz?.eligibilityStatus === 'NOT_ELIGIBLE' && (
        <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>
          Based on your last quiz you aren't eligible for GLP-1 treatment right now.
          Please consult a doctor first. <Link to="/quiz">Retake quiz</Link>.
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        isPatient || (!isAdmin && user?.role !== ROLES.STAFF) ? (
          <PatientCardView items={items} quiz={quiz} />
        ) : (
          <DataTable
            search
            searchPlaceholder="Search by package name…"
            rows={items}
            columns={[
              { key: 'name', header: 'Name', render: (r) => <div className="cell-strong">{r.name}</div> },
              {
                key: 'desc',
                header: 'Description',
                render: (r) => <span className="cell-muted">{r.description ? r.description.slice(0, 80) : '—'}</span>
              },
              {
                key: 'price',
                header: 'Price',
                render: (r) => <span className="cell-strong">{formatMoney(r.price, r.currency || 'MYR')}</span>
              },
              {
                key: 'tests',
                header: 'Tests',
                render: (r) => <span className="muted">{r.tests?.length || 0}</span>
              },
              {
                key: 'status',
                header: 'Status',
                render: (r) => r.isActive
                  ? <StatusBadge tone="success">Active</StatusBadge>
                  : <StatusBadge tone="neutral">Inactive</StatusBadge>
              },
              {
                key: 'actions',
                header: '',
                width: 130,
                render: (r) => (
                  <div className="row-actions">
                    <Link to={`/packages/${r._id}`} className="icon-btn" title="View"><Eye size={16} /></Link>
                    {isAdmin && (
                      <>
                        <Link to={`/packages/${r._id}/edit`} className="icon-btn" title="Edit"><Pencil size={16} /></Link>
                        <button className="icon-btn" title="Delete" onClick={() => setConfirming(r._id)}>
                          <Trash2 size={16} color="var(--danger)" />
                        </button>
                      </>
                    )}
                  </div>
                )
              }
            ]}
            emptyTitle="No packages yet"
            emptyMessage={isAdmin ? 'Create the first package to get started.' : 'Packages will appear here once available.'}
          />
        )
      )}

      <ConfirmModal
        open={Boolean(confirming)}
        title="Delete this package?"
        message="This cannot be undone. Existing orders linked to this package will keep a snapshot."
        confirmLabel="Delete package"
        tone="danger"
        loading={deleting}
        onCancel={() => setConfirming(null)}
        onConfirm={onDelete}
      />
    </>
  );
}

export default Packages;
